export type StringDevStyleTokens = Record<string, string>;

const STRING_DEV_DEFAULT_TOKENS: StringDevStyleTokens = {
  "--string-dev-panel-bg": "#ffffff",
  "--string-dev-panel-border": "1px solid #e0e0e0",
  "--string-dev-panel-radius": "16px",
  "--string-dev-panel-shadow": "0 16px 24px rgba(0, 0, 0, 0.1)",
  "--string-dev-panel-backdrop": "blur(12px)",
  "--string-dev-panel-padding": "4px",
  "--string-dev-panel-font-family":
    '-apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  "--string-dev-panel-font-size": "12px",
  "--string-dev-panel-text": "#111111",
  "--string-dev-panel-muted": "rgba(17, 17, 17, 0.6)",
  "--string-dev-panel-subtle": "rgba(17, 17, 17, 0.72)",
  "--string-dev-panel-divider": "rgba(17, 17, 17, 0.08)",
  "--string-dev-header-font-size": "10px",
  "--string-dev-header-font-weight": "500",
  "--string-dev-header-letter-spacing": "0",
  "--string-dev-header-padding": "8px 8px 8px",
  "--string-dev-header-margin-bottom": "4px",
  "--string-dev-accent": "#FF4F36",
  "--string-dev-accent-soft": "rgba(255, 79, 54, 0.12)",
  "--string-dev-accent-soft-hover": "rgba(255, 79, 54, 0.18)",
  "--string-dev-focus-color": "rgba(255, 79, 54, 0.68)",
  "--string-dev-focus-shadow": "0 0 0 2px rgba(255, 79, 54, 0.18)",
  "--string-dev-field-bg": "rgba(221, 221, 221, 0.4)",
  "--string-dev-field-border": "rgba(255, 255, 255, 0.96)",
  "--string-dev-field-radius": "8px",
  "--string-dev-input-height": "32px",
  "--string-dev-button-height": "32px",
  "--string-dev-chip-size": "24px",
  "--string-dev-icon-button-size": "20px",
  "--string-dev-icon-button-radius": "8px",
  "--string-dev-chip-active-bg": "rgba(221, 221, 221, 0.54)",
  "--string-dev-chip-muted": "rgba(17, 17, 17, 0.22)",
  "--string-dev-chip-resolved": "rgba(17, 17, 17, 0.78)",
  "--string-dev-icon-button-hover-bg": "rgba(17, 17, 17, 0.08)",
};

function indentCss(cssText: string, indent: string): string {
  return cssText
    .trim()
    .split("\n")
    .map((line) => `${indent}${line}`)
    .join("\n");
}

export function buildDevtoolsThemeBlock(
  selectors: string | string[],
  overrides: StringDevStyleTokens = {},
): string {
  const selectorText = Array.isArray(selectors) ? selectors.join(",\n") : selectors;
  const tokens = { ...STRING_DEV_DEFAULT_TOKENS, ...overrides };
  const declarations = Object.entries(tokens)
    .map(([name, value]) => `${name}: ${value};`)
    .join("\n");

  return `${selectorText} {\n${indentCss(declarations, "  ")}\n}`;
}

export function ensureStringDevtoolsSharedStyles(): HTMLStyleElement | null {
  if (typeof document === "undefined") {
    return null;
  }

  const styleId = "string-devtools-shared-styles";
  const existing = document.getElementById(styleId);
  if (existing instanceof HTMLStyleElement) {
    return existing;
  }

  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `
    ${buildDevtoolsThemeBlock("[data-string-devtools-theme]")}

    [data-string-devtools-panel],
    [data-string-devtools-button],
    [data-string-devtools-icon-button],
    [data-string-devtools-chip],
    [data-string-devtools-text-input] {
      box-sizing: border-box;
      font-family: var(--string-dev-panel-font-family);
    }

    [data-string-devtools-panel] {
      position: relative;
      background: var(--string-dev-panel-bg);
      backdrop-filter: var(--string-dev-panel-backdrop);
      border: var(--string-dev-panel-border);
      border-radius: var(--string-dev-panel-radius);
      padding: var(--string-dev-panel-padding);
      font-size: var(--string-dev-panel-font-size);
      color: var(--string-dev-panel-text);
      box-shadow: var(--string-dev-panel-shadow);
    }

    [data-string-devtools-panel-header] {
      display: grid;
      grid-template-columns: 16px minmax(0, 1fr) 16px;
      align-items: center;
      column-gap: 8px;
      padding: var(--string-dev-header-padding);
      margin-bottom: var(--string-dev-header-margin-bottom);
    }

    [data-string-devtools-panel-title] {
      display: block;
      grid-column: 2;
      justify-self: center;
      min-width: 0;
      font-size: var(--string-dev-header-font-size);
      font-weight: var(--string-dev-header-font-weight);
      color: var(--string-dev-panel-text);
      letter-spacing: var(--string-dev-header-letter-spacing);
      line-height: 1.2;
      text-align: center;
    }

    [data-string-devtools-panel-icon] {
      width: 16px;
      height: 16px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: var(--string-dev-panel-text);
    }

    [data-string-devtools-panel-icon] svg {
      width: 16px;
      height: 16px;
    }

    [data-string-devtools-icon-button] {
      width: var(--string-dev-icon-button-size);
      height: var(--string-dev-icon-button-size);
      padding: 0;
      border: 0;
      border-radius: var(--string-dev-icon-button-radius);
      background: transparent;
      color: var(--string-dev-panel-muted);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      appearance: none;
      cursor: pointer;
      transition:
        background 0.15s ease,
        color 0.15s ease,
        opacity 0.15s ease,
        box-shadow 0.15s ease;
    }

    [data-string-devtools-icon-button] svg {
      width: 16px;
      height: 16px;
    }

    [data-string-devtools-icon-button]:hover,
    [data-string-devtools-icon-button]:focus-visible {
      background: var(--string-dev-icon-button-hover-bg);
      color: var(--string-dev-panel-text);
      outline: none;
    }

    [data-string-devtools-button] {
      min-height: var(--string-dev-button-height);
      width: auto;
      padding: 0 8px;
      border: 1px solid var(--string-dev-field-border);
      border-radius: var(--string-dev-field-radius);
      background: var(--string-dev-field-bg);
      color: var(--string-dev-panel-text);
      font-size: var(--string-dev-panel-font-size);
      font-weight: 500;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      appearance: none;
      cursor: pointer;
      transition:
        background 0.15s ease,
        color 0.15s ease,
        border-color 0.15s ease,
        box-shadow 0.15s ease;
    }

    [data-string-devtools-button][data-full-width="true"] {
      width: 100%;
    }

    [data-string-devtools-button]:hover,
    [data-string-devtools-button]:focus-visible {
      background: color-mix(in srgb, var(--string-dev-field-bg) 78%, #ffffff 22%);
      border-color: color-mix(in srgb, var(--string-dev-field-border) 72%, var(--string-dev-panel-text) 28%);
      outline: none;
    }

    [data-string-devtools-button]:focus-visible {
      box-shadow: var(--string-dev-focus-shadow);
    }

    [data-string-devtools-text-input] {
      min-height: var(--string-dev-input-height);
      width: 100%;
      padding: 0 10px;
      border: 1px solid var(--string-dev-field-border);
      border-radius: var(--string-dev-field-radius);
      background: var(--string-dev-field-bg);
      color: var(--string-dev-panel-text);
      font-size: var(--string-dev-panel-font-size);
      font-weight: 500;
      outline: none;
      appearance: none;
    }

    [data-string-devtools-text-input]:focus {
      border-color: var(--string-dev-focus-color);
      box-shadow: var(--string-dev-focus-shadow);
    }

    [data-string-devtools-chip] {
      width: var(--string-dev-chip-size);
      height: var(--string-dev-chip-size);
      padding: 0;
      border: 0;
      border-radius: var(--string-dev-field-radius);
      background: transparent;
      color: var(--string-dev-chip-muted);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      appearance: none;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    }

    [data-string-devtools-chip] svg {
      width: 18px;
      height: 18px;
    }

    [data-string-devtools-chip]:hover,
    [data-string-devtools-chip]:focus-visible {
      background: var(--string-dev-icon-button-hover-bg);
      color: var(--string-dev-panel-text);
      outline: none;
    }

    [data-string-devtools-chip][data-selected="true"],
    [data-string-devtools-chip][data-open="true"],
    [data-string-devtools-chip][data-active="true"] {
      background: var(--string-dev-chip-active-bg);
      color: var(--string-dev-accent);
    }

    [data-string-devtools-chip][data-selected="false"][data-resolved="true"] {
      color: var(--string-dev-chip-resolved);
    }

    /* Standardized Overlay HUD System */
    [data-string-devtools-overlay-chrome] {
      position: absolute;
      top: 0;
      left: 0;
      width: 28px;
      height: 28px;
      pointer-events: none;
      overflow: visible;
      z-index: 10010;
      will-change: transform;
    }

    [data-string-devtools-overlay-chrome][hidden],
    [data-string-devtools-overlay-badge][hidden] {
      display: none !important;
    }

    [data-string-devtools-overlay-badge] {
      position: absolute;
      top: 0;
      left: 0;
      width: 28px;
      height: 28px;
      padding: 0;
      border: 1px solid rgba(255, 255, 255, 0.92);
      border-radius: 6px;
      background: rgba(255, 255, 255, 0.85);
      color: var(--string-dev-panel-text);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      pointer-events: auto;
      transition: background 0.15s ease, opacity 0.2s ease, box-shadow 0.15s ease, color 0.15s ease;
      will-change: transform;
    }

    [data-string-devtools-overlay-badge]:hover,
    [data-string-devtools-overlay-badge]:focus-visible {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
      outline: none;
    }

    [data-string-devtools-overlay-badge]:focus-visible {
      border-color: var(--string-dev-focus-color);
    }

    [data-string-devtools-overlay-badge][data-active="false"] {
      background: rgba(236, 236, 236, 0.5);
      border-color: rgba(236, 236, 236, 0.65);
    }

    [data-string-devtools-overlay-badge] svg {
      width: 16px;
      height: 16px;
      display: block;
    }

    [data-string-devtools-overlay-panel] {
      position: absolute;
      top: 0;
      left: calc(100% + 8px);
      width: 208px;
      opacity: 0;
      transform: translateY(4px);
      pointer-events: none;
      transition: opacity 0.2s ease, transform 0.2s ease;
      z-index: 10;
    }

    [data-string-devtools-overlay-panel][data-open="true"] {
      opacity: 1;
      transform: translateY(0);
      pointer-events: auto;
    }
  `;
  document.head.appendChild(style);
  return style;
}
